import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Loader2, RefreshCw, LogOut, Lock, CheckCircle2, Package } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const STATUS_STYLES = {
    paid: "text-green-400 border-green-400/40",
    awaiting_upi_confirmation: "text-yellow-400 border-yellow-400/40",
    pay_on_delivery: "text-sky-400 border-sky-400/40",
    pending: "text-stone-600 border-zinc-600",
};
const STATUS_LABELS = {
    paid: "PAID",
    awaiting_upi_confirmation: "UPI PENDING",
    pay_on_delivery: "CASH ON DELIVERY",
    pending: "UNPAID",
};

export default function OwnerDashboard() {
    const [authed, setAuthed] = useState(null);
    const [password, setPassword] = useState("");
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);

    const loadOrders = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/owner/orders`, { withCredentials: true });
            setOrders(data);
            setAuthed(true);
        } catch {
            setAuthed(false);
        }
    }, []);

    useEffect(() => {
        loadOrders();
    }, [loadOrders]);

    const login = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(`${API}/owner/login`, { password }, { withCredentials: true });
            setPassword("");
            await loadOrders();
            toast.success("Welcome back to the Vault");
        } catch {
            toast.error("Wrong password");
            setAuthed(false);
        } finally {
            setLoading(false);
        }
    };

    const markPaid = async (id) => {
        try {
            await axios.patch(`${API}/owner/orders/${id}`, { payment_status: "paid" }, { withCredentials: true });
            toast.success(`${id} marked as paid`);
            loadOrders();
        } catch {
            toast.error("Update failed — try again");
        }
    };

    const logout = async () => {
        await axios.post(`${API}/owner/logout`, {}, { withCredentials: true }).catch(() => {});
        setAuthed(false);
    };

    if (authed === null) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-vault-black text-stone-900">
                <Loader2 size={36} className="animate-spin text-vault-red" />
            </div>
        );
    }

    if (!authed) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-vault-black px-6 text-stone-900">
                <form
                    data-testid="owner-login-form"
                    onSubmit={login}
                    className="w-full max-w-sm border border-vault-border p-10"
                >
                    <Lock size={32} className="text-vault-red" />
                    <h1 className="mt-6 font-display text-3xl uppercase">Owner Access</h1>
                    <p className="mt-3 font-mono text-[10px] tracking-[0.25em] text-stone-500">
                        VAULT DEPORTIVOS — PRIVATE
                    </p>
                    <input
                        data-testid="owner-password-input"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Password"
                        className="mt-8 w-full border border-vault-border bg-transparent px-4 py-3 text-sm text-stone-900 placeholder-stone-400"
                    />
                    <button
                        data-testid="owner-login-button"
                        type="submit"
                        disabled={loading || !password}
                        className="btn-sweep mt-4 flex w-full items-center justify-center gap-2 border border-vault-red py-3.5 font-mono text-xs tracking-[0.25em] text-stone-900 disabled:opacity-40"
                    >
                        {loading ? <Loader2 size={14} className="animate-spin" /> : null} ENTER THE VAULT
                    </button>
                    <Link
                        data-testid="owner-back-home-link"
                        to="/"
                        className="mt-6 block text-center font-mono text-[10px] tracking-[0.3em] text-zinc-600 transition-colors hover:text-stone-900"
                    >
                        BACK TO SHOP
                    </Link>
                </form>
            </div>
        );
    }

    return (
        <div data-testid="owner-dashboard" className="min-h-screen bg-vault-black px-6 py-12 text-stone-900">
            <div className="mx-auto max-w-5xl">
                <div className="flex flex-wrap items-center justify-between gap-4 border-b border-vault-border pb-6">
                    <div>
                        <p className="font-mono text-xs tracking-[0.3em] text-vault-red">OWNER ONLY</p>
                        <h1 className="mt-2 font-display text-4xl uppercase md:text-5xl">Orders Vault</h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            data-testid="owner-refresh-button"
                            onClick={loadOrders}
                            aria-label="Refresh orders"
                            className="border border-vault-border p-3 text-stone-600 transition-colors hover:border-vault-red hover:text-stone-900"
                        >
                            <RefreshCw size={16} />
                        </button>
                        <button
                            data-testid="owner-logout-button"
                            onClick={logout}
                            aria-label="Log out"
                            className="border border-vault-border p-3 text-stone-600 transition-colors hover:border-vault-red hover:text-stone-900"
                        >
                            <LogOut size={16} />
                        </button>
                    </div>
                </div>

                {orders.length === 0 ? (
                    <p data-testid="owner-no-orders" className="mt-16 text-center font-mono text-xs tracking-[0.25em] text-stone-500">
                        NO ORDERS YET — SHARE THE SHOP
                    </p>
                ) : (
                    <div className="mt-8 space-y-4">
                        {orders.map((o) => (
                            <div
                                key={o.id}
                                data-testid={`order-row-${o.id}`}
                                className="border border-vault-border bg-vault-surface/30 p-6"
                            >
                                <div className="flex flex-wrap items-start justify-between gap-4">
                                    <div>
                                        <p className="font-mono text-sm tracking-[0.15em] text-vault-red">{o.id}</p>
                                        <p className="mt-1 font-mono text-[10px] tracking-[0.15em] text-stone-500">
                                            {new Date(o.created_at).toLocaleString("en-IN")}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="border border-vault-border px-3 py-1.5 font-mono text-[10px] tracking-[0.2em] text-stone-600">
                                            {o.payment_method === "upi" ? "UPI" : o.payment_method === "card" ? "CARD" : "COD"}
                                        </span>
                                        <span
                                            data-testid={`order-status-${o.id}`}
                                            className={`border px-3 py-1.5 font-mono text-[10px] tracking-[0.2em] ${STATUS_STYLES[o.payment_status] || STATUS_STYLES.pending}`}
                                        >
                                            {STATUS_LABELS[o.payment_status] || o.payment_status}
                                        </span>
                                        {o.payment_status === "awaiting_upi_confirmation" && (
                                            <button
                                                data-testid={`mark-paid-${o.id}`}
                                                onClick={() => markPaid(o.id)}
                                                className="flex items-center gap-2 border border-green-500/50 px-3 py-1.5 font-mono text-[10px] tracking-[0.2em] text-green-400 transition-colors hover:bg-green-500/10"
                                            >
                                                <CheckCircle2 size={12} /> MARK PAID
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="mt-5 grid gap-4 border-t border-vault-border pt-5 md:grid-cols-3">
                                    <div>
                                        <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] text-stone-500">
                                            <Package size={12} /> ITEMS
                                        </p>
                                        <ul className="mt-2 space-y-1 text-sm text-stone-600">
                                            {o.items.map((it, i) => (
                                                <li key={i}>
                                                    {it.name} — {it.size} × {it.qty}
                                                </li>
                                            ))}
                                        </ul>
                                        {o.custom_number && (
                                            <p
                                                data-testid={`order-shoe-number-${o.id}`}
                                                className="mt-2 font-mono text-[10px] tracking-[0.2em] text-vault-red"
                                            >
                                                SHOE NUMBER: {o.custom_number}
                                            </p>
                                        )}
                                    </div>
                                    <div>
                                        <p className="font-mono text-[10px] tracking-[0.25em] text-stone-500">CUSTOMER</p>
                                        <p className="mt-2 text-sm text-stone-600">{o.customer.name}</p>
                                        <p className="text-sm text-stone-500">{o.customer.email}</p>
                                        {o.customer.phone ? <p className="text-sm text-stone-500">{o.customer.phone}</p> : null}
                                        <p className="mt-1 text-sm text-stone-500">
                                            {o.customer.address}, {o.customer.city}
                                            {o.customer.postal_code ? ` ${o.customer.postal_code}` : ""},{" "}
                                            {o.customer.country}
                                        </p>
                                        {o.payment_proof && (
                                            <a
                                                data-testid={`proof-link-${o.id}`}
                                                href={`${API}/files/${o.payment_proof}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="mt-3 inline-block"
                                            >
                                                <img
                                                    src={`${API}/files/${o.payment_proof}`}
                                                    alt="Payment screenshot"
                                                    className="h-20 w-20 border border-vault-border object-cover transition-opacity hover:opacity-80"
                                                />
                                            </a>
                                        )}
                                    </div>
                                    <div className="md:text-right">
                                        <p className="font-mono text-[10px] tracking-[0.25em] text-stone-500">TOTAL</p>
                                        <p data-testid={`order-total-${o.id}`} className="mt-2 font-mono text-2xl text-stone-900">
                                            {inr(o.total)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
